import React from "react";
import { BUSINESS, ASSETS } from "../lib/assets";
import SectionHeading from "../components/SectionHeading";
import usePageMeta from "../hooks/usePageMeta";
import { MapPin, Phone, Clock } from "lucide-react";

const locations = [
  {
    name: "Spring Hill — Flagship",
    address: "16447 US-41, Spring Hill, FL 34610",
    phone: BUSINESS.phone,
    county: "Pasco County",
    hours: BUSINESS.hours,
    primary: true,
  },
];

export default function Locations() {
  usePageMeta({
    title: "Restaurant Locations",
    description: "Find When Pigs Fly Family BBQ at 16447 US-41, Spring Hill, FL. Hours, directions, phone number and online ordering — right on Florida's Nature Coast.",
    path: "/locations",
  });
  return (
    <div>
      <section className="border-b border-ink py-16 md:py-24" data-testid="locations-hero">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="eyebrow mb-3 text-ink/70">Come see us</div>
          <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">Restaurant Locations</h1>
          <p className="mt-6 max-w-2xl font-serif text-base leading-relaxed text-ink/80">
            Our home pit is in Spring Hill, FL. We cater across Pasco, Hernando, Citrus, Northern Pinellas and Hillsborough counties, and travel
            statewide for weddings and large events.
          </p>
        </div>
      </section>

      <section className="border-b border-ink py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="Where to find us" title="Three Ways To Eat" />
          <div className="mt-12 grid grid-cols-1 gap-0 border border-ink lg:grid-cols-3">
            {locations.map((loc, i) => {
              if (loc.type === "future") {
                return (
                  <div
                    key={`future-${i}`}
                    data-testid={`location-${i}`}
                    className={`grain-block relative overflow-hidden ${i < 2 ? "lg:border-r border-ink" : ""} ${i < 2 ? "border-b lg:border-b-0 border-ink" : ""} bg-ink`}
                  >
                    <img
                      src={loc.image}
                      alt={loc.alt}
                      className="h-full min-h-[380px] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                );
              }
              return (
                <div
                  key={loc.name}
                  data-testid={`location-${i}`}
                  className={`grain-block flex flex-col p-8 ${i < 2 ? "lg:border-r border-ink" : ""} ${i < 2 ? "border-b lg:border-b-0 border-ink" : ""} ${loc.primary ? "bg-ink text-bone" : "bg-bone"}`}
                >
                  <div className={`eyebrow mb-3 ${loc.primary ? "text-bone/70" : "text-ink/60"}`}>{loc.county}</div>
                  <h3 className="font-alfa text-2xl uppercase leading-tight">{loc.name}</h3>

                  <div className="mt-6 space-y-3 font-serif text-sm">
                    <div className="flex items-start gap-3"><MapPin size={16} className="mt-1 shrink-0" /><span>{loc.address}</span></div>
                    <div className="flex items-start gap-3"><Phone size={16} className="mt-1 shrink-0" /><a href={`tel:${loc.phone.replace(/[^0-9+]/g, "")}`} className="link-underline">{loc.phone}</a></div>
                  </div>

                  <div className="mt-6 border-t border-current pt-4">
                    <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">
                      <Clock size={12} /> Hours
                    </div>
                    <table className="w-full font-mono text-xs">
                      <tbody>
                        {loc.hours.map(([day, time]) => (
                          <tr key={day} className="border-b border-current/20 last:border-b-0">
                            <td className="py-1.5 uppercase opacity-70">{day}</td>
                            <td className="py-1.5 text-right">{time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] opacity-60">Holidays may vary</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="grain-block relative aspect-[16/10] w-full overflow-hidden border border-ink shadow-hard">
                <iframe
                  title="Map — 16447 US-41, Spring Hill, FL 34610"
                  src="https://www.google.com/maps?q=16447+US-41,+Spring+Hill,+FL+34610&output=embed"
                  className="mono-img h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="border border-ink p-6">
                <div className="eyebrow mb-2 text-ink/60">Flagship</div>
                <h4 className="font-alfa text-3xl uppercase leading-none">Spring Hill, FL</h4>
                <p className="mt-4 font-serif text-sm text-ink/80">
                  Our home. Come by for lunch, dinner, or takeout. Every plate is smoked on-site.
                </p>
                <div className="mt-6 space-y-2 font-mono text-xs uppercase tracking-[0.16em]">
                  <p>16447 US-41 · Spring Hill · FL 34610</p>
                  <p>{BUSINESS.phone}</p>
                </div>
              </div>
              <div className="mt-8">
                <img src={ASSETS.smokerCloseup} alt="Our smoker" className="mono-img aspect-video w-full border border-ink object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
